import { useTranslation } from 'react-i18next';
import { OrderCancelRequestDto } from '@tiendanube/common';
import { Reason, Status, Domain } from '@tiendanube/common/src/enums';
import {
  Select,
  Checkbox,
  Alert,
  Stack,
  Link,
  Text,
} from '@tiendanube/components';
import { ExternalLinkIcon } from '@tiendanube/icons';
import ModalAside from 'App/components/lab/ModalAside';
import { CancelAndSaveButtons, Input } from 'commons/components';
import { HOW_TO_CANCEL_AN_ORDER_LINKS } from 'commons/constants';
import useOrderCancel, {
  DEFAULT_CANCEL_VALUES,
  CUSTOM_REASON_MAX_LENGTH,
} from 'commons/hooks/useOrderCancel';
import { useGetOrdersByIds } from 'domains/Orders/Orders/hooks';
import OrderCancelLabels from './OrderCancelLabels';

interface OrderCancelModalProps {
  show: boolean;
  hideModal: () => void;
  orderIds: string[];
  isLoading: boolean;
  appliedAction: (action: OrderCancelRequestDto) => void;
}

const cancelOrder = {
  reasons: [
    Reason.CUSTOMER,
    Reason.INVENTORY,
    Reason.FRAUD,
    Reason.TEST,
    Reason.OTHER,
  ],
  defaultValues: DEFAULT_CANCEL_VALUES,
};

function OrderCancelModal({
  show,
  hideModal,
  orderIds,
  appliedAction,
  isLoading,
}: OrderCancelModalProps): JSX.Element {
  const {
    t,
    i18n: { language },
  } = useTranslation([Domain.ORDERS]);
  const {
    cancelOrderState,
    handleSelectChange,
    handleCheckboxChange,
    handleCustomReasonChange,
    isValidToCancel,
    selectRef,
    textareaRef,
  } = useOrderCancel({
    defaultValues: cancelOrder.defaultValues,
    isOpen: show,
  });

  const orders = useGetOrdersByIds(orderIds);
  const reasonOptions = cancelOrder.reasons.map((option) => ({
    label: t(`cancelModal.reason.options.${option}`, {
      count: orders.length,
    }),
    value: option,
  }));

  const onClickPrimary = async () => {
    await appliedAction(cancelOrderState);
  };

  const alertHasAOrderPaid = orders.some(
    (order) =>
      order.payment.status === Status.PAID && order.status === Status.OPEN,
  );

  return (
    <ModalAside
      isOpen={show}
      onDidDismiss={hideModal}
      headerContent={t('cancelModal.title', {
        count: orderIds.length,
        orderId: orders[0]?.number,
      })}
    >
      <Stack column spacing="tight" align="stretch">
        {alertHasAOrderPaid ? (
          <Stack.Item>
            <Alert
              appearance="warning"
              title={t('cancelModal.alertPaidTitle')}
              show
            >
              <Stack column align="flex-start">
                <Text size="caption">
                  {t('cancelModal.alertPaidDescription')}
                </Text>
                <Link
                  href={HOW_TO_CANCEL_AN_ORDER_LINKS[language]}
                  size="small"
                  icon={ExternalLinkIcon}
                  underline
                >
                  {t('cancelModal.alertPaidLink')}
                </Link>
              </Stack>
            </Alert>
          </Stack.Item>
        ) : null}
        {orders.length > 1 && (
          <OrderCancelLabels
            orders={orders}
            bulkSelectedOrdersCount={orderIds.length}
          />
        )}
        <Stack.Item />
        <Stack.Item>
          <div ref={selectRef}>
            <Select
              label={t('cancelModal.reason.label')}
              name="reason"
              options={reasonOptions}
              value={cancelOrderState.reason}
              onChange={handleSelectChange}
            />
          </div>
        </Stack.Item>
        {cancelOrderState.reason === Reason.OTHER && (
          <Stack.Item>
            <div ref={textareaRef}>
              <Input
                type="textarea"
                label={t('cancelModal.customReason.label')}
                placeholder={t('cancelModal.customReason.placeholder')}
                name="customReason"
                value={cancelOrderState.customReason}
                onChange={handleCustomReasonChange}
                maxLength={CUSTOM_REASON_MAX_LENGTH}
                helpText={t('cancelModal.customReason.counter', {
                  current: cancelOrderState.customReason?.length || 0,
                  max: CUSTOM_REASON_MAX_LENGTH,
                })}
                helpTextColor="neutral-textDisabled"
                lines={3}
              />
            </div>
          </Stack.Item>
        )}
        <Stack.Item />
        <Stack.Item>
          <Checkbox
            label={t('cancelModal.email', {
              count: orders.length,
            })}
            name="email"
            onChange={handleCheckboxChange}
            checked={cancelOrderState.email}
          />
        </Stack.Item>
        <Stack.Item>
          <Checkbox
            label={t('cancelModal.restock')}
            name="restock"
            onChange={handleCheckboxChange}
            checked={cancelOrderState.restock}
          />
        </Stack.Item>
        <Stack.Item />
        <Stack.Item>
          <CancelAndSaveButtons
            saveText={t('cancelModal.actionPrimary', {
              count: orders.length,
            })}
            onSave={onClickPrimary}
            onCancel={hideModal}
            cancelText={t('cancelModal.actionSecondary')}
            isDisabled={isLoading || !isValidToCancel}
            isLoading={isLoading}
          />
        </Stack.Item>
      </Stack>
    </ModalAside>
  );
}

export default OrderCancelModal;
